import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from "@mui/material";
import { api } from "../../api/apiService";

type AppItem = {
  slug: string;
  name: string;
  description: string;
  logo_url: string;
  price_amount: number | null;
  price_unit: string;
  price_currency: string;
  builtin: boolean;
  enabled: boolean;
};

function errorText(err: unknown): string {
  const anyErr = err as { data?: unknown; response?: { data?: unknown }; message?: string };
  const data = anyErr?.data ?? anyErr?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  return anyErr?.message || "Request failed";
}

function priceLine(app: AppItem): string {
  const unit = (app.price_unit || "per_use").replace(/_/g, " ");
  if (app.price_amount == null) return "Price not set · " + unit;
  const currency = app.price_currency || "";
  return (currency ? currency + " " : "") + app.price_amount + " · " + unit;
}

export function ApplicationsPage({ embedded = false }: { embedded?: boolean }) {
  const [apps, setApps] = useState<AppItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [logoURL, setLogoURL] = useState("");
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("per_use");
  const [currency, setCurrency] = useState("EUR");

  const load = async () => setApps(await api.get("/api/applications"));

  useEffect(() => {
    load().catch((err) => setError(errorText(err)));
  }, []);

  const savePrice = async (app: AppItem, amount: string) => {
    setError(null);
    try {
      await api.put("/api/applications/" + app.slug, {
        price_amount: amount === "" ? null : Number(amount),
        price_unit: app.price_unit,
        price_currency: currency,
      });
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <Box sx={{ p: embedded ? 0 : 3, maxWidth: 980, color: "#fafafa" }}>
      <Typography variant="h5" gutterBottom>Applications</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Register a module with a logo, a short description, and a price per use. The amount can stay empty until you set a rate.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 2, mb: 4 }}>
        {apps.map((app) => (
          <Card key={app.slug} variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                {app.logo_url ? (
                  <Box component="img" src={app.logo_url} alt="" sx={{ width: 48, height: 48 }} />
                ) : null}
                <Typography variant="subtitle1">{app.name}</Typography>
              </Stack>
              <Typography variant="body2" sx={{ mb: 1 }}>{app.description}</Typography>
              <Typography variant="body2" color="text.secondary">{priceLine(app)}</Typography>
              <Typography variant="caption" display="block" sx={{ mb: 1 }}>{app.enabled ? "On" : "Off"} · {app.builtin ? "Catalog" : "Yours"}</Typography>
              <Button size="small" onClick={() => {
                const next = window.prompt("Price amount", app.price_amount == null ? "" : String(app.price_amount));
                if (next === null || next.trim() === "") return;
                void savePrice(app, next);
              }}>
                Set price
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Typography variant="h6" gutterBottom>Register an application</Typography>
      <Stack spacing={2} sx={{ maxWidth: 480 }}>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} multiline minRows={2} />
        <TextField label="Logo URL" value={logoURL} onChange={(e) => setLogoURL(e.target.value)} />
        <TextField label="Price amount" value={price} onChange={(e) => setPrice(e.target.value)} />
        <TextField label="Price unit" value={unit} onChange={(e) => setUnit(e.target.value)} helperText="per_completion, per_match, per_render, per_use" />
        <TextField label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)} />
        <Button variant="contained" sx={{ alignSelf: "flex-start" }} onClick={async () => {
          setError(null);
          try {
            await api.post("/api/applications", {
              name,
              description,
              logo_url: logoURL,
              price_amount: price === "" ? null : Number(price),
              price_unit: unit,
              price_currency: currency,
            });
            setName("");
            setDescription("");
            setLogoURL("");
            setPrice("");
            await load();
          } catch (err) {
            setError(errorText(err));
          }
        }}>Register</Button>
      </Stack>
    </Box>
  );
}
